// A list of store products by pid: the chosen ones in order (move, remove) and
// a search that adds more. Searches the StoreProduct CRUD the way the product
// list does (by name or pid; the site's search resolver adds what it knows —
// another language's name, an ISBN).
import React, { useEffect, useRef, useState } from "react"
import { ArrowUpIcon, ArrowDownIcon, XMarkIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import { api } from "aldoo-ra/API"

const crud = api("crud")
const SELECT = "pid name thumbnail enabled"

const imageOf = (product) => (typeof product?.thumbnail === "string" && /^(\/|https?:)/.test(product.thumbnail) ? product.thumbnail : null)
const asList = (res) => (Array.isArray(res) ? res : res?.data || [])

/** The products of these pids, as a Map pid → product. */
const loadProducts = async (pids) => {
  if (!pids.length) return new Map()
  const res = await crud({ get: { model: "StoreProduct", match: { pid: { $in: pids }, deleted: { $ne: true } }, select: SELECT, itemsPerPage: pids.length } })
  return new Map(asList(res).map((product) => [product.pid, product]))
}

const searchProducts = async (text) => {
  const res = await crud({ get: { model: "StoreProduct", match: { deleted: { $ne: true } }, search: { $or: [{ name: text }, { pid: text }] }, select: SELECT, itemsPerPage: 12, sort: { name: 1 } } })
  return asList(res)
}

function ProductLine({ product, pid, children }) {
  const image = imageOf(product)
  return (
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div className="w-10 h-10 rounded bg-admin_neutral_1 overflow-hidden shrink-0">{image && <img src={image} alt="" className="w-full h-full object-cover" />}</div>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-admin_text truncate">{product?.name || pid}</span>
        <span className="text-xs text-admin_text_secondary truncate">
          {pid}
          {product?.enabled === false && " · disabled"}
          {!product && " · not found"}
        </span>
      </div>
      {children}
    </div>
  )
}

export default function ProductListPicker({ value, onChange, exclude, placeholder = "Search products by name or pid…", testId }) {
  const pids = Array.isArray(value) ? value.filter((pid) => typeof pid === "string" && pid) : []
  const [known, setKnown] = useState(new Map())
  const [text, setText] = useState("")
  const [results, setResults] = useState([])
  const [busy, setBusy] = useState(false)
  const run = useRef(0)

  const missing = pids.filter((pid) => !known.has(pid))
  const missingKey = missing.join(",")
  useEffect(() => {
    if (!missing.length) return
    let alive = true
    loadProducts(missing)
      .then((found) => alive && setKnown((current) => new Map([...current, ...found, ...missing.filter((pid) => !found.has(pid)).map((pid) => [pid, null])])))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [missingKey])

  // a search a moment after the typing stops; the last one wins
  useEffect(() => {
    const term = text.trim()
    if (term.length < 2) return setResults([])
    const id = ++run.current
    setBusy(true)
    const timer = setTimeout(async () => {
      const found = await searchProducts(term).catch(() => [])
      if (id !== run.current) return
      setResults(found)
      setBusy(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [text])

  const add = (product) => {
    setKnown((current) => new Map(current).set(product.pid, product))
    onChange?.([...pids, product.pid])
    setText("")
  }
  const remove = (pid) => onChange?.(pids.filter((entry) => entry !== pid))
  const move = (index, step) => {
    const next = [...pids]
    const [entry] = next.splice(index, 1)
    next.splice(index + step, 0, entry)
    onChange?.(next)
  }

  const offered = results.filter((product) => !pids.includes(product.pid) && product.pid !== exclude)

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {pids.length > 0 && (
        <div className="flex flex-col divide-y divide-admin_bordered border border-admin_bordered rounded-lg">
          {pids.map((pid, index) => (
            <div key={pid} className="flex items-center gap-2 p-2" data-testid="picked-product">
              <ProductLine product={known.get(pid)} pid={pid} />
              <button type="button" disabled={index === 0} onClick={() => move(index, -1)} className="p-1 rounded text-admin_text_secondary hover:text-admin_text disabled:opacity-30" aria-label="Move up">
                <ArrowUpIcon className="w-4 h-4" />
              </button>
              <button type="button" disabled={index === pids.length - 1} onClick={() => move(index, 1)} className="p-1 rounded text-admin_text_secondary hover:text-admin_text disabled:opacity-30" aria-label="Move down">
                <ArrowDownIcon className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => remove(pid)} className="p-1 rounded text-admin_text_secondary hover:text-admin_error" aria-label="Remove">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <label className="relative block">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-admin_text_secondary pointer-events-none" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 pl-10 pr-3 rounded-lg border border-admin_bordered bg-admin_paper text-admin_text text-sm outline-none focus:border-admin_primary"
          data-testid={testId ? `${testId}-search` : undefined}
        />
      </label>

      {text.trim().length >= 2 && (
        <div className="flex flex-col border border-admin_bordered rounded-lg max-h-[320px] overflow-y-auto">
          {busy && <Typography variant="admin_body2" className="p-3 my-0 opacity-70">Searching…</Typography>}
          {!busy && !offered.length && <Typography variant="admin_body2" className="p-3 my-0 opacity-70">Nothing found.</Typography>}
          {!busy &&
            offered.map((product) => (
              <button key={product.pid} type="button" onClick={() => add(product)} className="flex items-center gap-2 p-2 text-left hover:bg-admin_neutral_1" data-testid="product-result">
                <ProductLine product={product} pid={product.pid} />
                <PlusIcon className="w-4 h-4 text-admin_primary shrink-0" />
              </button>
            ))}
        </div>
      )}
    </div>
  )
}
